import React from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Types & Constants
// ─────────────────────────────────────────────────────────────────────────────

export type Env = 'dev' | 'staging' | 'prod';

export function getAdminApiUrl(env: Env): string {
  switch (env) {
    case 'prod':    return process.env.REACT_APP_PROD_API_URL || '';
    case 'staging': return process.env.REACT_APP_STAGING_API_URL || '';
    case 'dev':     return process.env.REACT_APP_DEV_API_URL || '';
  }
}

/**
 * Returns the WAF dev-key header value for non-prod environments. The dev and
 * staging API Gateway WAFs require an `x-ss-dev-key` header (configured in
 * `infra/sam/stacks/waf/template.yaml`). Empty string in env → omit header.
 */
export function getEnvHeaders(env: Env): Record<string, string> {
  if (env === 'prod') return {};
  const key = process.env.REACT_APP_WAF_DEV_KEY;
  return key ? { 'x-ss-dev-key': key } : {};
}

export const ENV_COLORS: Record<Env, { pill: string; badge: string }> = {
  dev:     { pill: 'bg-blue-950/60 border-blue-700 text-blue-300',    badge: 'bg-blue-950/60 border-blue-700/50 text-blue-300' },
  staging: { pill: 'bg-amber-950/60 border-amber-700 text-amber-300', badge: 'bg-amber-950/60 border-amber-700/50 text-amber-300' },
  prod:    { pill: 'bg-red-950/60 border-red-700 text-red-300',       badge: 'bg-red-950/60 border-red-700/50 text-red-300' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared UI Primitives
// ─────────────────────────────────────────────────────────────────────────────

export function Spinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const s = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
  return (
    <svg className={`${s} animate-spin text-violet-500`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
    </svg>
  );
}

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 p-3 bg-red-950/40 border border-red-800/50 rounded-xl">
      <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" /><path d="M12 8v4m0 4h.01" />
      </svg>
      <p className="text-sm text-red-300">{message}</p>
    </div>
  );
}

export function SuccessBanner({ message }: { message: string }) {
  return (
    <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-emerald-950/50 border border-emerald-800/50 rounded-xl">
      <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p className="text-sm text-emerald-300">{message}</p>
    </div>
  );
}
